// ----------------------------------------------------------------------------
// Section: USP Banner
// ----------------------------------------------------------------------------
//
// Hosts USP Banner Item blocks (src/blocks/usp-banner-item.liquid) as a
// horizontal band that is either a continuously scrolling marquee or a
// static row, depending on the "Auto scroll" setting, whether the items
// overflow the available width, and prefers-reduced-motion.
//
// `content_for 'blocks'` can only be rendered once per host location without
// duplicating block editor attributes (breaks theme-editor block selection),
// so unlike a plain settings-array marquee (e.g. logo-list), the duplicate
// "loop" track needed for continuous scrolling is built here by cloning the
// live track's DOM rather than by looping over the blocks twice in Liquid.
// The clone is stripped of any `data-shopify-*` attributes and marked
// `aria-hidden` so each statement is still announced once to assistive tech
// however many times it's drawn, and so the theme editor only ever treats
// the live track's items as selectable.
//
// Seamlessness does NOT depend on the track being at least as wide as the
// viewport (logo-list's `min-width: 100%` trick, which only holds when the
// track is narrower than the viewport). Instead the two tracks sit in a
// `width: max-content` wrapper and the keyframe moves it exactly `-50%` —
// i.e. exactly one track's own width, whatever that is — so the loop is
// gapless whether the items are too few to fill the viewport or wide enough
// to overflow it many times over. See _usp-banner.scss.
//
// Pause/resume on hover/focus is pure CSS (`animation-play-state`), which
// already resumes from the same frame rather than restarting — no JS needed
// for that half of the contract.
//
// ----------------------------------------------------------------------------

import './_usp-banner.scss';

// -------------------------------------
// Variables
// -------------------------------------

var SECTION_SELECTOR = '[data-section-type="usp-banner"]';
var CLIP_SELECTOR = '[data-usp-banner-clip]';
var VIEWPORT_SELECTOR = '[data-usp-banner-viewport]';
var TRACK_SELECTOR = '[data-usp-banner-track]';
var SCROLLING_CLASS = 'usp-banner--scrolling';
var STATIC_CLASS = 'usp-banner--static';
var RESIZE_DEBOUNCE_MS = 150;
var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

// -------------------------------------
// Helpers
// -------------------------------------

function stripShopifyAttributes(el) {
  [...el.querySelectorAll('*'), el].forEach(node => {
    [...node.attributes].forEach(attr => {
      if (attr.name === 'id' || attr.name.startsWith('data-shopify')) {
        node.removeAttribute(attr.name);
      }
    });
  });
}
function removeDuplicateTrack(viewport) {
  viewport.querySelectorAll("".concat(TRACK_SELECTOR, "[aria-hidden=\"true\"]")).forEach(duplicate => duplicate.remove());
}
function addDuplicateTrack(viewport, track) {
  if (viewport.querySelector("".concat(TRACK_SELECTOR, "[aria-hidden=\"true\"]"))) return;
  var duplicate = track.cloneNode(true);
  duplicate.setAttribute('aria-hidden', 'true');
  stripShopifyAttributes(duplicate);
  viewport.appendChild(duplicate);
}

// -------------------------------------
// Section
// -------------------------------------

function initSection(sectionEl) {
  var clip = sectionEl.querySelector(CLIP_SELECTOR);
  var viewport = sectionEl.querySelector(VIEWPORT_SELECTOR);
  var track = sectionEl.querySelector(TRACK_SELECTOR);
  if (!clip || !viewport || !track) return;
  var autoScroll = sectionEl.dataset.autoScroll === 'true';
  function evaluateState() {
    // Reset to the single-track, static-width baseline before measuring
    // so a stale duplicate or a previous scrolling run (where `clip`
    // switches to full-bleed, see _usp-banner.scss) never skews the
    // overflow check. Whether items overflow is always decided against
    // the page-width bound — only once that's true does the width
    // expand to full-bleed for the scroll effect itself.
    removeDuplicateTrack(viewport);
    sectionEl.classList.remove(SCROLLING_CLASS, STATIC_CLASS);
    var overflows = track.scrollWidth > clip.clientWidth;
    var shouldScroll = autoScroll && overflows && !reducedMotionQuery.matches;
    if (shouldScroll) {
      addDuplicateTrack(viewport, track);
      sectionEl.classList.add(SCROLLING_CLASS);
      sectionEl.classList.remove(STATIC_CLASS);
    } else {
      sectionEl.classList.add(STATIC_CLASS);
      sectionEl.classList.remove(SCROLLING_CLASS);
    }
  }
  evaluateState();
  var resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(evaluateState, RESIZE_DEBOUNCE_MS);
  });
  reducedMotionQuery.addEventListener('change', evaluateState);
}

// -------------------------------------
// Hooks
// -------------------------------------

document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
document.addEventListener('shopify:section:load', e => {
  var sectionEl = e.target.querySelector(SECTION_SELECTOR);
  if (sectionEl) initSection(sectionEl);
});